import { User, CourseProgress, ActivityLog, Attempt, CreateUserParams, UserRecord } from "@nicsmith/shared_types";

export interface UserAiCostItem {
  uid: string;
  user_id: number;
  name: string | null;
  email: string | null;
  chat_tokens: number;
  chat_prompt_tokens: number;
  chat_completion_tokens: number;
  chat_requests: number;
  chat_cost_usd: number;
  translation_count: number;
}

export interface AiCostsResponse {
  month: string;
  total_cost_usd: number;
  total_tokens: number;
  total_translations: number;
  users: UserAiCostItem[];
}
import { baseApi } from "./api";
import { UserProgressSummary } from "./progressApi";

export interface ActivityLogResponse {
  items: ActivityLog[];
  cursor: string | null;
}

export interface CuratorDashboardTopStudent {
  uid: string;
  id: number;
  name: string | null;
  avatar: string | null;
  exercises_completed: number;
  courses_enrolled: number;
  last_login: string | null;
}

export interface CuratorDashboardActivityItem {
  user_uid: string;
  user_name: string | null;
  user_avatar: string | null;
  event_type: string;
  description: string;
  created_at: string;
}

export interface CuratorDashboardResponse {
  total_students: number;
  active_this_week: number;
  with_activity_this_week: number;
  top_students: CuratorDashboardTopStudent[];
  recent_activity: CuratorDashboardActivityItem[];
}

export interface ModuleWithProgress {
  id: number;
  name: string;
  order_index: number;
  exercise_count: number;
  status: "not_started" | "in_progress" | "completed";
  exercises_completed: number;
  exercises_total: number;
  completion_percentage: number;
  time_spent_seconds: number;
}

export interface ExerciseWithProgress {
  id: number;
  name: string;
  order_index: number;
  type: string | null;
  difficulty: number;
  status: "not_started" | "in_progress" | "completed";
  best_score: number;
  total_attempts: number;
  time_spent_seconds: number;
}

export interface AttemptDetail {
  id: number;
  exercise_id: number;
  answers: unknown;
  score: number;
  max_score: number;
  time_spent_seconds: number;
  is_correct: boolean;
  feedback: unknown;
  created_at: string;
}

export const userApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // User authentication (login or signup)
    updateUser: builder.mutation<User, { id: string; data: Partial<User> }>({
      query: ({ id, data }) => ({
        url: `/users/${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: ["UserDetail", "Users"],
    }),
    getAllUsers: builder.query<User[], void>({
      query: () => ({
        url: `/users`,
      }),
      providesTags: ["Users"],
    }),
    getUserById: builder.query<User, { id: string }>({
      query: ({ id }) => ({
        url: `/users/${id}`,
      }),
      providesTags: ["UserDetail"],
    }),
    getUserByUid: builder.query<User, { uid: string }>({
      query: ({ uid }) => ({
        url: `/users/uid/${uid}`,
      }),
    }),

    // Admin: user progress endpoints
    getAdminUserProgressSummary: builder.query<
      UserProgressSummary,
      { userId: string }
    >({
      query: ({ userId }) => ({
        url: `/users/${userId}/progress/summary`,
      }),
      providesTags: ["UserDetail"],
    }),
    getAdminUserProgress: builder.query<CourseProgress[], { userId: string }>({
      query: ({ userId }) => ({
        url: `/users/${userId}/progress`,
      }),
      providesTags: ["UserDetail"],
    }),
    getAdminUserModuleProgress: builder.query<
      ModuleWithProgress[],
      { userId: string; courseId: number }
    >({
      query: ({ userId, courseId }) => ({
        url: `/users/${userId}/progress/course/${courseId}/modules`,
      }),
    }),
    getAdminUserExerciseProgress: builder.query<
      ExerciseWithProgress[],
      { userId: string; courseId: number; moduleId: number }
    >({
      query: ({ userId, courseId, moduleId }) => ({
        url: `/users/${userId}/progress/course/${courseId}/module/${moduleId}/exercises`,
      }),
    }),
    getAdminUserExerciseAttempts: builder.query<
      AttemptDetail[],
      { userId: string; courseId: number; moduleId: number; exerciseId: number }
    >({
      query: ({ userId, courseId, moduleId, exerciseId }) =>
        `/users/${userId}/progress/course/${courseId}/module/${moduleId}/exercise/${exerciseId}/attempts`,
    }),
    // Same endpoint typed as Attempt[] for reuse with learner-style components
    getAdminUserAttemptsAsAttempts: builder.query<
      Attempt[],
      { userId: string; courseId: number; moduleId: number; exerciseId: number }
    >({
      query: ({ userId, courseId, moduleId, exerciseId }) =>
        `/users/${userId}/progress/course/${courseId}/module/${moduleId}/exercise/${exerciseId}/attempts`,
    }),

    // Admin user management endpoints
    adminEnrollUser: builder.mutation<{ success: boolean }, { userId: string; courseId: number }>({
      query: ({ userId, courseId }) => ({
        url: `/users/${userId}/enroll`,
        method: "POST",
        body: { course_id: courseId },
      }),
      invalidatesTags: ["UserDetail", "UserActivity"],
    }),
    adminUnenrollUser: builder.mutation<{ success: boolean }, { userId: string; courseId: number }>({
      query: ({ userId, courseId }) => ({
        url: `/users/${userId}/enroll/${courseId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["UserDetail", "UserActivity"],
    }),
    adminUpdateUserTier: builder.mutation<{ success: boolean }, { userId: string; tier: string }>({
      query: ({ userId, tier }) => ({
        url: `/users/${userId}/tier`,
        method: "PATCH",
        body: { tier },
      }),
      invalidatesTags: ["UserDetail", "UserActivity"],
    }),
    adminUpdateUserRole: builder.mutation<{ success: boolean }, { userId: string; role: string }>({
      query: ({ userId, role }) => ({
        url: `/users/${userId}/role`,
        method: "PATCH",
        body: { role },
      }),
      invalidatesTags: ["UserDetail", "UserActivity"],
    }),
    adminUpdateUserStatus: builder.mutation<{ success: boolean }, { userId: string; is_active: boolean }>({
      query: ({ userId, is_active }) => ({
        url: `/users/${userId}/status`,
        method: "PATCH",
        body: { is_active },
      }),
      invalidatesTags: ["UserDetail", "UserActivity"],
    }),
    adminResetPassword: builder.mutation<{ success: boolean }, { userId: string }>({
      query: ({ userId }) => ({
        url: `/users/${userId}/reset-password`,
        method: "POST",
      }),
    }),
    adminForceSignOut: builder.mutation<{ success: boolean }, { userId: string }>({
      query: ({ userId }) => ({
        url: `/users/${userId}/sign-out`,
        method: "POST",
      }),
    }),
    getAdminUserActivity: builder.query<
      ActivityLogResponse,
      { userId: string; event_type?: string; from_date?: string; to_date?: string; limit?: number; cursor?: string }
    >({
      query: ({ userId, ...params }) => {
        const searchParams = new URLSearchParams();
        if (params.event_type) searchParams.append("event_type", params.event_type);
        if (params.from_date) searchParams.append("from_date", params.from_date);
        if (params.to_date) searchParams.append("to_date", params.to_date);
        if (params.limit) searchParams.append("limit", params.limit.toString());
        if (params.cursor) searchParams.append("cursor", params.cursor);
        const qs = searchParams.toString();
        return `/users/${userId}/activity${qs ? `?${qs}` : ""}`;
      },
      providesTags: ["UserActivity"],
    }),
    createAdminNote: builder.mutation<{ success: boolean }, { userId: string; note: string }>({
      query: ({ userId, note }) => ({
        url: `/users/${userId}/activity`,
        method: "POST",
        body: { note },
      }),
      invalidatesTags: ["UserActivity"],
    }),
    adminAssignCurator: builder.mutation<{ success: boolean }, { userId: string; curator_uid: string | null }>({
      query: ({ userId, curator_uid }) => ({
        url: `/users/${userId}/curator`,
        method: "PATCH",
        body: { curator_uid },
      }),
      invalidatesTags: ["UserDetail", "UserActivity"],
    }),
    // Admin: get enrollment map (uid -> courseIds[])
    getEnrollmentMap: builder.query<Record<string, number[]>, void>({
      query: () => `/users/enrollments`,
      providesTags: ["Users"],
    }),
    inviteUser: builder.mutation<UserRecord, CreateUserParams>({
      query: (payload) => ({
        url: "/auth/signup",
        method: "POST",
        body: payload,
      }),
    }),
    resendInvite: builder.mutation<{ message: string }, { email: string }>({
      query: ({ email }) => ({
        url: "/auth/resend-invite",
        method: "POST",
        body: { email },
      }),
    }),
    getAiCosts: builder.query<AiCostsResponse, { month?: string } | void>({
      query: (params) => {
        const month = params && "month" in params ? params.month : undefined;
        return `/users/ai-costs${month ? `?month=${month}` : ""}`;
      },
    }),
    getCuratorDashboard: builder.query<CuratorDashboardResponse, void>({
      query: () => `/users/curator-dashboard`,
      providesTags: ["Users"],
    }),
  }),
  overrideExisting: true,
});

// Export hooks for usage in components
export const {
  useUpdateUserMutation,
  useGetAllUsersQuery,
  useGetUserByIdQuery,
  useGetUserByUidQuery,
  useGetAdminUserProgressSummaryQuery,
  useGetAdminUserProgressQuery,
  useGetAdminUserModuleProgressQuery,
  useGetAdminUserExerciseProgressQuery,
  useGetAdminUserExerciseAttemptsQuery,
  useGetAdminUserAttemptsAsAttemptsQuery,
  useAdminEnrollUserMutation,
  useAdminUnenrollUserMutation,
  useAdminUpdateUserTierMutation,
  useAdminUpdateUserRoleMutation,
  useAdminUpdateUserStatusMutation,
  useAdminResetPasswordMutation,
  useAdminForceSignOutMutation,
  useGetAdminUserActivityQuery,
  useCreateAdminNoteMutation,
  useAdminAssignCuratorMutation,
  useGetEnrollmentMapQuery,
  useInviteUserMutation,
  useResendInviteMutation,
  useGetAiCostsQuery,
  useGetCuratorDashboardQuery,
} = userApi;
